import moment from 'moment';
import numeral from 'numeral';
import { Location } from 'history';
import { Locale } from 'components/locale';
import { WireAccount } from 'api/payments/wires.service';
import { CURRENCY_TYPES, PAYMENT_TYPES, TIME_FRAME, WIRE_ACTION_TEMPLATE } from 'constants.js';
import template from 'lodash/template';
import { lt } from 'utils/validations/gluValidations';
import { State } from '../logic';
import { ActionName } from '../WireContext';
import { WireFormShape } from '../WireFormProvider';
import {
  domestic,
  international,
  usdCurrencyType,
  WIRE_BANK_CODE_CONFIG
} from '../international/international.constants';
import defaultLocale from '../../locale/defaultLocale';
import { ACTION, AMOUNT_TYPE, CURRENCY_FORMAT } from '../common/common.constants';

type GetTitle = (
  action: string,
  location: Location<{ actionTitle?: string; workflowStartPath?: string }>
) => string;

type GetDisplayAmount = (params: {
  action: string;
  state: State;
  values: WireFormShape;
}) => string;

type Titles = { [key: string]: string };

type IsDateAvailableCheck = (
  availableDays: string[],
  selectedDate: string,
  /** Makes function return true or false instead of glu's undefined / ['error message'] for errors */
  useAsFlag?: boolean
) => undefined | string[] | boolean;

type IsInvalidBalanceCheckParams = {
  amount: number;
  balance: number;
  locale: Locale;
  transactionDate: string;
};

type IsInvalidBalanceCheck = (params: IsInvalidBalanceCheckParams) => undefined | string[];

type SortObject = { [key: string]: string; name: string };

type WireConfigType =
  | typeof WIRE_BANK_CODE_CONFIG.INTERNATIONAL
  | typeof WIRE_BANK_CODE_CONFIG.BOTH
  | typeof WIRE_BANK_CODE_CONFIG.DOMESTIC;

export const getDecimalPlaces = (
  currency: string,
  fxRates: { currency: string; allowedDecimals: number }[]
) => fxRates?.find((fx) => fx.currency === currency)?.allowedDecimals ?? 2;

export const formatCurrencyAmount = (value: string | number, decimalPlaces: number = 2) =>
  Intl.NumberFormat('en-US', {
    minimumFractionDigits: decimalPlaces,
    maximumFractionDigits: decimalPlaces
  }).format(value);

export const formatCurrencyForInput =
  (decimalPlaces: number = 2, symbol: string = '.', amount: undefined | string) =>
  (value: string | number) => {
    const isInitialLoad = amount === undefined;
    const matches = String(value)?.match(/[0-9]/g);
    const sanitized = matches?.reduce((acc, curr) => acc + curr, '').padStart(decimalPlaces, '0');
    const charactersFromEnd = decimalPlaces * -1;

    if (sanitized && decimalPlaces === 0) {
      return formatCurrencyAmount(sanitized, decimalPlaces);
    }

    if (sanitized) {
      const integral = sanitized.slice(0, charactersFromEnd);
      const fractional = sanitized.slice(charactersFromEnd);
      return formatCurrencyAmount(`${integral}${symbol}${fractional}`, decimalPlaces);
    }

    return isInitialLoad ? '' : formatCurrencyAmount(0, decimalPlaces);
  };

export const getTitle: GetTitle = (action = ACTION.DEFAULT, location) => {
  // If router state has an override
  if (location.state && location.state.actionTitle) return location.state.actionTitle;
  const titles: Titles = {
    create: defaultLocale['wires.titles.createWirePayment'],
    copy: defaultLocale['wires.titles.copyWirePayment'],
    default: '',
    initiate: defaultLocale['wires.titles.initiateWirePayment'],
    modify: defaultLocale['wires.titles.modifyWirePayment'],
    template: defaultLocale['wires.titles.createWireTemplate'],
    'template-modify': defaultLocale['wires.titles.modifyWireTemplate'],
    'template-view': defaultLocale['wires.titles.viewWireTemplate'],
    'template-copy': defaultLocale['wires.titles.copyWireTemplate'],
    'template-copy-payment': defaultLocale['wires.titles.createWireTemplate'],
    view: defaultLocale['wires.titles.viewWirePayment']
  };
  const title = titles[action];
  return title;
};

/** Checks string against allowed characters */
export const passesAllowedCharacters = (allowedCharacters: string, string: string) =>
  new RegExp(`[^${allowedCharacters}]`).test(string);

export const formatAccountName = (account: WireAccount) => {
  const accountDisplayName = account.nickname || account.name || account.displayName;
  const accountDisplayNumber = account.number ? ` - ${account.number}` : '';
  const name = `${accountDisplayName}${accountDisplayNumber} (${
    account.currency === CURRENCY_TYPES.US_DOLLAR.currency
      ? defaultLocale['common.currencySymbol']
      : ''
  }${numeral(account.balance).format(CURRENCY_FORMAT)}${
    account.currency !== CURRENCY_TYPES.US_DOLLAR.currency ? ` ${account.currency}` : ''
  } ${defaultLocale['common.available']})`;
  return { ...account, path: [], accountName: account?.name, name };
};

export const isInternational = (values: WireFormShape, state: State): boolean => {
  return (
    values?.paymentType?.[0]?.id?.toLowerCase() ===
      PAYMENT_TYPES.WIRE.INTERNATIONAL.toLowerCase() ||
    state?.viewResponse?.paymentType?.toLowerCase() ===
      PAYMENT_TYPES.WIRE.INTERNATIONAL.toLowerCase() ||
    state?.viewTemplateResponse?.[0]?.paymentType?.toLowerCase() ===
      PAYMENT_TYPES.WIRE.INTERNATIONAL.toLowerCase()
  );
};

export const isInternationalFx = (values: WireFormShape, state: State): boolean => {
  return (
    isInternational(values, state) &&
    values?.currencyType?.[0]?.currency !== usdCurrencyType[0].currency
  );
};

const amountUSDEquivalent = (
  action: string,
  amount: number,
  values: WireFormShape,
  decimalPlaces: number = 2
) => {
  const exchangeRate = numeral(
    // Use currency's default exchange rate when user's exchange rate is unavailable
    values?.exchangeRate || values?.currencyType?.[0]?.exchangeRate
  ).value();
  const calculatedAmount = amount / numeral(exchangeRate).value();
  // If user is on template, don't consider exchange rate and show us dollar amount
  if (action.includes(WIRE_ACTION_TEMPLATE))
    return `${defaultLocale['common.currencySymbol']}${formatCurrencyAmount(amount, 2)}`;
  return `${formatCurrencyAmount(calculatedAmount, decimalPlaces)} ${
    values?.currencyType?.[0]?.currency
  }`;
};

/** Get correct amount string for domestic and international payments */
export const getDisplayAmount: GetDisplayAmount = ({ action, state, values = {} }) => {
  const { amountType = [], currencyType } = values;
  const { fxRates, viewResponse, viewTemplateResponse } = state || {};

  const currentAmountType =
    amountType?.[0]?.id || viewTemplateResponse?.[0]?.amountType || viewResponse?.amountType;

  let amount = numeral(values?.amount || 0).value();
  const currency =
    currencyType?.[0]?.id || viewResponse?.currency || viewTemplateResponse?.[0]?.currency || '';
  const currencyDecimalPlace =
    fxRates?.find((fx) => fx.currency === currency)?.allowedDecimals ?? 2;

  if (action === ActionName.VIEW || action === ActionName.TEMPLATE_VIEW) {
    // NOTE: using creditAmount for FX international wires (should always show the sending amount)
    amount = numeral(
      viewResponse?.creditAmount || viewResponse?.amount || viewTemplateResponse?.[0]?.amount || 0
    ).value();

    if (currency !== CURRENCY_TYPES.US_DOLLAR.currency) {
      if (
        action === ActionName.TEMPLATE_VIEW &&
        currentAmountType === AMOUNT_TYPE.USD_EQUIVALENT
      ) {
        return amountUSDEquivalent(action, amount, values, 2);
      }

      return `${formatCurrencyAmount(
        amount,
        viewResponse?.currDecimals ?? currencyDecimalPlace
      )} ${currency}`;
    }
    return `${defaultLocale['common.currencySymbol']}${numeral(amount).format(CURRENCY_FORMAT)}`;
  }
  // Handle foreign amounts for international wires when not USD
  if (
    isInternational(values, state) &&
    currencyType?.[0]?.id !== CURRENCY_TYPES.US_DOLLAR.currency
  ) {
    // If user selects USD equivalent then perform conversion calculation to get send amount
    if (amountType?.[0]?.id === AMOUNT_TYPE.USD_EQUIVALENT) {
      return `${amountUSDEquivalent(action, amount, values, currencyDecimalPlace)}`;
    }

    // If user selects foreign amount then we don't need to convert anything
    return `${formatCurrencyAmount(amount, currencyDecimalPlace)} ${currencyType?.[0]?.currency}`;
  }
  // Handle amounts like normal
  const displayAmount = `${defaultLocale['common.currencySymbol']}${numeral(amount).format(
    CURRENCY_FORMAT
  )}`;
  return displayAmount;
};

export const isDateAvailableCheck: IsDateAvailableCheck = (
  availableDays = [],
  selectedDate,
  useAsFlag = false
) => {
  const days = availableDays.map((date) => moment(date));
  const passesCheck = days.some((date) => date.isSame(moment(selectedDate), TIME_FRAME.DAY));
  if (useAsFlag) return passesCheck;
  return passesCheck ? undefined : [defaultLocale['common.invalidDate']];
};

export const isInvalidBalanceCheck: IsInvalidBalanceCheck = ({
  amount,
  balance,
  locale,
  transactionDate
}) => {
  if (!moment(transactionDate).isSame(moment(), TIME_FRAME.DAY)) return undefined;
  // TODO: FX Payment checks
  return amount > balance ? [locale['wire.insufficientFunds']] : undefined;
};

export const scrollToError = () => {
  // TODO: Need to select dropdown or date picker errors somehow
  const error =
    document.querySelector('.has-error') || document.querySelector('input[error*=" "]') || window;
  return error instanceof HTMLElement ? window.scroll(0, error.offsetTop) : window.scroll(0, 0);
};

export const sortByName = (a: SortObject, b: SortObject) => {
  const textA = a?.name.toUpperCase();
  const textB = b?.name.toUpperCase();
  return textA < textB ? -1 : textA > textB ? 1 : 0;
};

export const maxLengthCheck = (
  length = 35,
  locale = defaultLocale,
  text = 'validation.maxNCharacters'
) =>
  lt({
    maxLength: length,
    msg: template(locale[text])({ max: length })
  });

export interface BankCodeMapper {
  address1?: string | undefined;
  address2?: string | undefined;
  address3?: string | undefined;
  bankName?: string | undefined;
  order?: number | undefined;
  paymentMethod?: string | undefined;
  routingNumber?: string | undefined;
  bankCode?: string | undefined;
}

export const bankCodeMapper = (bankInfo: BankCodeMapper) =>
  bankInfo
    ? [
        {
          ...bankInfo,
          addressLine1: bankInfo?.address1 || '',
          addressLine2: bankInfo?.address2 || '',
          addressLine3: bankInfo?.address3 || '',
          bankCode: bankInfo?.bankCode || bankInfo?.routingNumber || '',
          bankName: bankInfo?.bankName || '',
          bankRouteNumber: bankInfo?.routingNumber || '',
          // If bankCode is null or "" then use routingNumber (api isn't sending bankCode for retrieveWirePayment intermediaries)
          id: bankInfo?.bankCode || bankInfo?.routingNumber || '',
          name:
            bankInfo?.routingNumber && bankInfo?.bankName
              ? `${bankInfo?.routingNumber} - ${bankInfo?.bankName}`
              : '',
          paymentMethod: bankInfo?.paymentMethod || ''
        }
      ]
    : undefined;

export const getBankCodeType = (
  wireConfig: WireConfigType,
  currentPaymentMethod: 'international' | 'domestic' | undefined = undefined
) => {
  const allowedWireConfigs = [
    WIRE_BANK_CODE_CONFIG.INTERNATIONAL,
    WIRE_BANK_CODE_CONFIG.BOTH
  ] as unknown as Array<WireConfigType>;

  if (currentPaymentMethod && wireConfig === WIRE_BANK_CODE_CONFIG.BOTH) {
    return currentPaymentMethod;
  }
  return allowedWireConfigs.includes(wireConfig) ? international : domestic;
};
